import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import App from "./App";
import { CloudLogin } from "./components/CloudLogin";
import { migrateLocalToSupabase } from "./data/migrateLocalToSupabase";
import { supabaseProvider } from "./data/supabaseProvider";
import { syncAppDataToSupabase } from "./data/syncAppDataToSupabase";
import {
  ensureWorkspace,
  hasWorldwardenAccess,
  type WorkspaceAccess,
} from "./data/workspace";
import { starfallAuthUrl } from "./lib/config";
import { loadData, parseBackup } from "./lib/store";
import { requireSupabase } from "./lib/supabase";
import type { AppData } from "./types";

type BootstrapStatus =
  | "checking-session"
  | "checking-access"
  | "loading-workspace"
  | "ready"
  | "no-access"
  | "error";

type SaveStatus = "saved" | "saving" | "error";

function hasMeaningfulData(data: AppData): boolean {
  return (
    data.worlds.length > 0 ||
    data.entities.length > 0 ||
    data.relationships.length > 0 ||
    data.campaigns.length > 0 ||
    data.maps.length > 0 ||
    data.itemDefinitions.length > 0 ||
    data.itemInstances.length > 0 ||
    data.creatureDefinitions.length > 0 ||
    data.currencies.length > 0
  );
}

function CloudPanel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="cloud-gate">
      <section className="cloud-card">
        <div className="brand-mark cloud-mark">✦</div>
        <div className="eyebrow">STARFALL WORKS / WORLDWARDEN</div>
        <h1>{title}</h1>
        {children}
      </section>
    </main>
  );
}

function MigrationPanel({
  localData,
  workspace,
  onComplete,
  onSkip,
}: {
  localData: AppData;
  workspace: WorkspaceAccess;
  onComplete: (data: AppData) => void;
  onSkip: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [backupData, setBackupData] = useState<AppData | null>(null);
  const [backupName, setBackupName] = useState("");

  const hasBrowserData = hasMeaningfulData(localData);

  async function migrate(dataToMigrate: AppData) {
    setBusy(true);
    setError("");

    try {
      await migrateLocalToSupabase(dataToMigrate, workspace.workspaceId);

      const cloudData = await supabaseProvider.load({
        workspaceId: workspace.workspaceId,
      });

      onComplete(cloudData);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Migration failed.");
    } finally {
      setBusy(false);
    }
  }

  async function chooseBackup(file: File | undefined) {
    setError("");
    setBackupData(null);
    setBackupName("");

    if (!file) return;

    try {
      const parsed = parseBackup(await file.text());
      setBackupData(parsed);
      setBackupName(file.name);
    } catch {
      setError("That file is not a valid Worldwarden JSON backup.");
    }
  }

  return (
    <CloudPanel title="Set up persistent Worldwarden data">
      <p>
        This persistent workspace is empty. You can bring in data from this
        browser, import a Worldwarden JSON backup from another site or start
        with an empty cloud workspace.
      </p>

      {hasBrowserData && (
        <div className="cloud-migration-option">
          <h2>Use data from this browser</h2>
          <p>
            Copy the Worldwarden data already stored in this browser into
            Supabase. The local copy will remain untouched as a safety backup.
          </p>
          <button
            className="primary"
            onClick={() => void migrate(localData)}
            disabled={busy}
          >
            {busy ? "Migrating…" : "Migrate Browser Data"}
          </button>
        </div>
      )}

      <div className="cloud-migration-option">
        <h2>Import a JSON backup</h2>
        <p>
          Use this when your existing data lives on another Worldwarden domain
          or browser. Create a Backup JSON file there, then select it here.
        </p>

        <input
          type="file"
          accept=".json,application/json"
          disabled={busy}
          onChange={(event) => void chooseBackup(event.target.files?.[0])}
        />

        {backupName && (
          <p className="cloud-note">
            Ready to import: <strong>{backupName}</strong>
          </p>
        )}

        <button
          className="primary"
          onClick={() => backupData && void migrate(backupData)}
          disabled={busy || !backupData}
        >
          {busy ? "Importing…" : "Import Backup to Cloud"}
        </button>
      </div>

      <div className="cloud-migration-option">
        <h2>Start fresh</h2>
        <p>
          Leave any browser data or backup files untouched and begin with an
          empty persistent workspace.
        </p>
        <button className="secondary" onClick={onSkip} disabled={busy}>
          Start with Empty Cloud Workspace
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}
    </CloudPanel>
  );
}

export default function CloudWorldwarden() {
  const db = requireSupabase();
  const localData = useMemo(() => loadData(), []);

  const [session, setSession] = useState<Session | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [status, setStatus] = useState<BootstrapStatus>("checking-session");
  const [error, setError] = useState("");
  const [workspace, setWorkspace] = useState<WorkspaceAccess | null>(null);
  const [cloudData, setCloudData] = useState<AppData | null>(null);
  const [skipMigration, setSkipMigration] = useState(false);
  const [revision, setRevision] = useState(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const persistChain = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    let active = true;

    void db.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setAuthChecked(true);
    });

    const {
      data: { subscription },
    } = db.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
      setAuthChecked(true);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [db]);

  useEffect(() => {
    if (!authChecked) return;

    if (!session) {
      setStatus("checking-session");
      setWorkspace(null);
      setCloudData(null);
      return;
    }

    let active = true;

    async function bootstrap() {
      try {
        setError("");
        setStatus("checking-access");

        const allowed = await hasWorldwardenAccess(session.user.id);

        if (!allowed) {
          if (active) setStatus("no-access");
          return;
        }

        setStatus("loading-workspace");

        const nextWorkspace = await ensureWorkspace(session);
        const nextData = await supabaseProvider.load({
          workspaceId: nextWorkspace.workspaceId,
        });

        if (!active) return;

        setWorkspace(nextWorkspace);
        setCloudData(nextData);
        setStatus("ready");
      } catch (reason) {
        if (!active) return;

        setError(
          reason instanceof Error
            ? reason.message
            : "Worldwarden could not initialize persistent storage.",
        );
        setStatus("error");
      }
    }

    void bootstrap();

    return () => {
      active = false;
    };
  }, [authChecked, session]);

  const persist = useCallback(
    async (nextData: AppData) => {
      if (!workspace) return;

      setSaveStatus("saving");

      persistChain.current = persistChain.current
        .catch(() => undefined)
        .then(() => syncAppDataToSupabase(nextData, workspace.workspaceId));

      try {
        await persistChain.current;
        setSaveStatus("saved");
      } catch (reason) {
        console.error("Worldwarden persistence failed", reason);
        setSaveStatus("error");
      }
    },
    [workspace],
  );

  if (!authChecked) {
    return (
      <CloudPanel title="Opening Worldwarden">
        <p>Checking your Starfall Works session…</p>
      </CloudPanel>
    );
  }

  if (!session) {
    return <CloudLogin />;
  }

  if (status === "no-access") {
    return (
      <CloudPanel title="Worldwarden access is not enabled">
        <p>
          This Starfall Works account does not currently have Worldwarden
          access. Ask a platform administrator to grant the entitlement.
        </p>

        <a className="cloud-link" href={`${starfallAuthUrl}/dashboard`}>
          Return to Starfall Works
        </a>
      </CloudPanel>
    );
  }

  if (status === "error") {
    return (
      <CloudPanel title="Worldwarden could not start">
        <div className="error-box">{error}</div>

        <button className="secondary" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </CloudPanel>
    );
  }

  if (status !== "ready" || !workspace || !cloudData) {
    return (
      <CloudPanel title="Opening Worldwarden">
        <p>
          {status === "checking-access"
            ? "Checking product access…"
            : "Loading your persistent workspace…"}
        </p>
      </CloudPanel>
    );
  }

  const shouldOfferMigration =
    !skipMigration &&
    !hasMeaningfulData(cloudData);

  if (shouldOfferMigration) {
    return (
      <MigrationPanel
        localData={localData}
        workspace={workspace}
        onComplete={(nextData) => {
          setCloudData(nextData);
          setRevision((value) => value + 1);
        }}
        onSkip={() => setSkipMigration(true)}
      />
    );
  }

  const persistenceLabel =
    saveStatus === "saving"
      ? "Cloud · Saving…"
      : saveStatus === "error"
        ? "Cloud · Save error"
        : "Cloud · Saved";

  return (
    <App
      key={`${workspace.workspaceId}:${revision}`}
      initialData={cloudData}
      onDataChange={persist}
      keepLocalBackup={false}
      persistenceLabel={persistenceLabel}
    />
  );
}
